import { isItRole } from '@helpdesk/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from './api.ts';
import type { Session, User } from './types.ts';

export const sessionKey = ['session'] as const;

export function useSession() {
  const query = useQuery({
    queryKey: sessionKey,
    queryFn: () => api.get<Session>('/api/auth/session'),
    staleTime: 5 * 60_000,
  });
  const user = query.data?.user ?? null;
  return {
    user,
    isIt: !!user && isItRole(user.role),
    isAdmin: user?.role === 'admin',
    setupRequired: query.data?.setupRequired ?? false,
    isLoading: query.isPending,
  };
}

/** After signing in or out: store the new user and drop everything cached for the old one. */
export function useSetSessionUser() {
  const client = useQueryClient();
  return useCallback(
    (user: User | null) => {
      // A session request sent before sign-in could otherwise land afterwards
      // and overwrite the new user with "signed out".
      void client.cancelQueries({ queryKey: sessionKey });
      client.removeQueries({ predicate: (q) => q.queryKey[0] !== 'session' });
      client.setQueryData<Session>(sessionKey, (old) => ({
        setupRequired: old?.setupRequired ?? false,
        user,
      }));
    },
    [client],
  );
}

export async function signOut(setUser: (u: User | null) => void) {
  await api.post('/api/auth/logout').catch(() => undefined);
  setUser(null);
}
